import React from 'react';
import { productTour } from './builders';

const BACK = { backTo: '/investments/equity', backLabel: 'Back to Equity' };

// Product pages under Investments → Equity.
export const EQUITY_TOURS = {
  'inv-equity-etf': productTour({
    name: 'Equity ETFs',
    intro: (
      <>
        <p>Exchange-traded funds hold a basket of shares, such as the Nifty 50, and trade on the stock exchange like a single share.</p>
        <p>Add each purchase here and Mickles tracks what you paid and what it's worth today.</p>
      </>
    ),
    add: (
      <>
        <p>Enter the ETF, the purchase date, the <b>price</b> per unit and the <b>number of units</b>. The amount invested is worked out for you.</p>
        <p>Use the ETF's <b>NSE symbol</b> as its name (for example <b>NIFTYBEES</b>) so live prices can be found. A 5-digit number is treated as a BSE code. Bought more later? Pick the same name from the list to add to it.</p>
      </>
    ),
    live: (
      <p>Fetches today's price for each ETF from the exchange and shows its current value and profit or loss. If an ETF shows no price, check that its name is the exchange symbol.</p>
    ),
    ...BACK,
  }),

  'inv-equity-stocks': productTour({
    name: 'Stocks',
    intro: (
      <>
        <p>Track the shares you own in individual companies, such as Reliance or TCS.</p>
        <p>Add each purchase here, check today's prices, and record sales to see your profit or loss.</p>
      </>
    ),
    add: (
      <>
        <p>Enter the <b>Stock Name</b>, the purchase date, the <b>Price</b> per share and the <b>Number of Units</b>. The <b>Amount Invested</b> is worked out for you.</p>
        <p>Type the stock's <b>NSE symbol</b> as its name (for example <b>RELIANCE</b> or <b>TCS</b>), not the full company name, so live prices can be found. A 5-digit number is treated as a BSE code. Bought more later? Pick the same name from <b>Your Stocks</b>.</p>
      </>
    ),
    live: (
      <p>Tap <b>Get Live Price</b> to fetch today's price for each stock from the exchange. Prices aren't loaded automatically. If a stock shows no price, check that its name is the exchange symbol.</p>
    ),
    holdings: (
      <>
        <p>One row per stock, combining all your purchases of it. Once you fetch live prices you'll see its <b>Current Value</b> and <b>P/L</b> (profit or loss).</p>
        <p><b>Click a row</b> to see every buy and sale, and edit or delete one there with the <b>⋮</b> menu. Tap a column heading to sort.</p>
      </>
    ),
    charts: (
      <p><b>Current Distribution</b> shows how your stocks are split by today's value, and <b>Invested Distribution</b> by what you paid. Hover or tap a slice to see its amount.</p>
    ),
    extraSteps: [
      {
        target: '[data-tour="inv-stocks-sell"]',
        title: 'Sell shares',
        body: (
          <>
            <p>Sold some shares? Tap the <b>sell</b> icon on that stock's row and enter the <b>Sale Price per Unit</b>, the <b>Number of Units to Sell</b> and any <b>Charges</b> (brokerage, taxes).</p>
            <p>Your oldest shares are counted as sold first (called FIFO), so <b>Profit/Loss</b> is worked out against what you paid for those. The sale appears in the stock's history and the units leave your holdings.</p>
          </>
        ),
      },
      {
        target: '[data-tour="inv-stocks-plreport"]',
        title: 'P/L Report',
        body: (
          <p>Every sale you've recorded in one place: sale price, charges, the amount you received and the <b>profit or loss</b> on each, with your overall total at the top.</p>
        ),
      },
    ],
    ...BACK,
  }),

  'inv-equity-stocks-pl-report': [
    {
      title: 'Stock P&L Report',
      body: (
        <>
          <p>Every sale you've recorded on the <b>Stocks</b> page, in one place: what you received, what it cost you, and the profit or loss on each.</p>
          <p>To add a sale, use the <b>sell</b> icon on the Stocks page.</p>
        </>
      ),
    },
    {
      target: '[data-tour="inv-plr-summary"]',
      title: 'Your totals',
      body: (
        <ul>
          <li><b>Total Sales Amount</b>: sale price × units, across all sales.</li>
          <li><b>Total Charges</b>: brokerage, taxes and other fees.</li>
          <li><b>Net Amount Received</b>: what reached you after charges.</li>
          <li><b>Total P&amp;L</b>: overall profit or loss, with how many sales made a profit or a loss.</li>
        </ul>
      ),
    },
    {
      target: '[data-tour="inv-plr-return"]',
      title: 'Return',
      body: (
        <>
          <p>Your overall profit or loss as a % of what the sold shares cost you.</p>
          <p>Below it, <b>per year</b> shows how fast that money grew, taking into account when you bought and sold. Hover or tap it to see how it's worked out.</p>
        </>
      ),
    },
    {
      target: '[data-tour="inv-plr-table"]',
      title: 'Your sales',
      body: (
        <p>One row per sale, newest first. Tap a column heading to sort, for example by <b>P&amp;L</b> to see your best and worst trades.</p>
      ),
    },
    {
      target: '[data-tour="inv-plr-peryear"]',
      title: 'Return and Per Year',
      body: (
        <ul>
          <li><b>Return</b>: that sale's profit or loss as a % of what the shares cost.</li>
          <li><b>Per Year</b>: the return per year, with how long you held the shares. Held a year or more, it's compounded (CAGR); less than a year, it's the simple yearly rate.</li>
          <li>Sales recorded before buy dates were saved show <b>—</b> per year.</li>
        </ul>
      ),
    },
    {
      target: '[data-tour="inv-plr-actions"]',
      title: 'Fix a sale',
      body: (
        <ul>
          <li><b>Edit</b> (pencil): change the date, price, units or charges. You'll see the new profit or loss before saving. Changing units updates your holdings, oldest shares first.</li>
          <li><b>Delete</b> (bin): removes the sale and puts the shares back into your holdings at their average buy price.</li>
        </ul>
      ),
    },
    {
      title: "You're all set",
      body: (
        <p>Record each sale on the Stocks page and this report keeps itself up to date. Replay this guide any time with the <b>Guide</b> button.</p>
      ),
      action: { label: 'Back to Stocks', to: '/investments/equity/stocks' },
    },
  ],

  'inv-equity-mutual-fund-equity': productTour({
    name: 'Equity Mutual Funds',
    intro: (
      <>
        <p>Equity mutual funds pool money from many investors and invest it in company shares. You own <b>units</b> of the fund, priced at its NAV (price per unit).</p>
        <p>Add each lump-sum purchase or SIP instalment here to track what you paid and what it's worth today.</p>
      </>
    ),
    add: (
      <>
        <p>Enter the <b>Name of Fund</b>, its <b>Symbol Code</b>, the purchase date, the <b>NAV</b> you bought at and the <b>Amount Invested</b>. The <b>Number of Units</b> is worked out for you.</p>
        <p>The <b>Symbol Code</b> is required and is used to look up the live NAV. Use the fund's code from Yahoo Finance, which usually looks like <b>0P0000XXXX.BO</b>. Bought more later? Pick the same fund name from the list.</p>
      </>
    ),
    live: (
      <p>Tap <b>Get Live NAV</b> to fetch the latest NAV of each fund using its symbol code. NAVs aren't loaded automatically. If a fund shows no NAV, its symbol code is probably wrong.</p>
    ),
    holdings: (
      <>
        <p>One row per fund, combining all your purchases of it. Once you fetch live NAVs you'll see its <b>Current Value</b> and <b>P/L</b> (profit or loss).</p>
        <p><b>Click a row</b> to see every purchase and edit or delete one with the <b>⋮</b> menu. Got the symbol wrong? Tap <b>Edit Symbol</b> there to fix it for every purchase of that fund.</p>
      </>
    ),
    charts: (
      <p><b>Current Distribution</b> shows how your funds are split by today's value, and <b>Invested Distribution</b> by what you paid. Hover or tap a slice to see its amount.</p>
    ),
    ...BACK,
  }),

  'inv-equity-nps': productTour({
    name: 'National Pension System (NPS)',
    intro: (
      <>
        <p>NPS is a government-backed retirement scheme. A pension fund manager invests your money across schemes such as <b>E</b> (equity), <b>C</b> (corporate bonds) and <b>G</b> (government bonds).</p>
        <p>Record each contribution here to see how much you've put in and where it's invested.</p>
      </>
    ),
    add: (
      <>
        <p>Enter the <b>Name of Scheme</b>, the date, the <b>NAV</b> (price per unit) on that date and the <b>Amount Invested</b>. The <b>Number of Units</b> is worked out for you.</p>
        <p>Your NPS statement shows the NAV for each contribution. Give each scheme its own name (for example <b>SBI Scheme E Tier I</b>) and pick the same name from the list next time.</p>
      </>
    ),
    summary: (
      <p><b>Total</b> is everything you've contributed, at the price you paid. Mickles doesn't fetch live NPS values, so this isn't today's market value.</p>
    ),
    holdings: (
      <>
        <p>One row per scheme, with the amount you've put in (<b>Value</b>) and its share of your NPS total (<b>Allocation</b>).</p>
        <p><b>Click a row</b> to see every contribution, and edit or delete one there with the <b>⋮</b> menu.</p>
      </>
    ),
    charts: (
      <p>How your NPS contributions are split between schemes. Hover or tap a slice to see its amount.</p>
    ),
    extraSteps: [
      {
        target: '[data-tour="inv-nps-refresh"]',
        title: 'Refresh',
        body: (
          <p>The <b>refresh</b> icon reloads your saved NPS entries. It doesn't fetch new NAVs.</p>
        ),
      },
    ],
    ...BACK,
  }),
};
